import React from 'react';
import { ShieldCheck, ShieldAlert, Check, FileText, X } from 'lucide-react';

const PROTECT_PDF_URL = '/avenant-protect-plus.pdf';

const COVERED = ['Casse accidentelle', 'Dégât des eaux / intempéries', 'Incendie', 'Vandalisme', 'Vol caractérisé'];

export const ProtectPlusSelector = ({ standardCaution, monthlyPrice, config, selected, onSelect, accepted, onAccept }) => {
  if (!config) return null;
  const savings = standardCaution - config.reducedCaution;

  return (
    <div className="mt-6 bg-slate-900 rounded-2xl p-5 border border-[#DFFF00]/30" data-testid="protect-plus-selector">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-[#DFFF00]" />
        <h3 className="font-bold text-white text-lg">Protégez votre matériel</h3>
      </div>
      <p className="text-slate-400 text-sm mb-4">Choisissez votre niveau de couverture et réduisez votre caution.</p>

      <div className="grid md:grid-cols-2 gap-3">
        {/* Standard */}
        <button
          type="button"
          onClick={() => onSelect(false)}
          data-testid="protect-option-standard"
          className={`text-left rounded-xl p-4 border transition-colors ${!selected ? 'border-white bg-slate-800' : 'border-slate-700 bg-slate-800/40 hover:border-slate-500'}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-white flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-slate-400" />Caution classique</span>
            {!selected && <span className="text-xs bg-white text-slate-900 rounded-full px-2 py-0.5 font-semibold">Choisi</span>}
          </div>
          <p className="text-slate-300 text-sm">Caution bloquée de <span className="font-bold text-white">{standardCaution}€</span></p>
          <p className="text-slate-500 text-xs mt-1">Aucun forfait mensuel. Vous êtes responsable de la valeur totale du matériel en cas de problème.</p>
        </button>

        {/* Protect+ */}
        <button
          type="button"
          onClick={() => onSelect(true)}
          data-testid="protect-option-plus"
          className={`text-left rounded-xl p-4 border-2 transition-colors relative ${selected ? 'border-[#DFFF00] bg-[#DFFF00]/10' : 'border-[#DFFF00]/40 bg-slate-800/40 hover:border-[#DFFF00]'}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-[#DFFF00] flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Protect +</span>
            {selected
              ? <span className="text-xs bg-[#DFFF00] text-slate-900 rounded-full px-2 py-0.5 font-semibold">Choisi</span>
              : <span className="text-xs bg-[#DFFF00]/20 text-[#DFFF00] rounded-full px-2 py-0.5 font-semibold">Recommandé</span>}
          </div>
          <p className="text-slate-200 text-sm">
            Caution réduite à <span className="font-bold text-white">{config.reducedCaution}€</span>
            <span className="ml-1 text-emerald-400 font-semibold">(-{savings}€)</span>
          </p>
          <p className="text-slate-200 text-sm">+ <span className="font-bold text-white">{config.fee}€/mois</span></p>
          <p className="text-slate-400 text-xs mt-1">Vous immobilisez {savings}€ de moins. Franchise : 375€ (casse/dégâts) · 750€ (vol).</p>
        </button>
      </div>

      {/* Coverage detail when Protect+ selected */}
      {selected && (
        <div className="mt-4 bg-slate-800/60 rounded-xl p-4 border border-slate-700" data-testid="protect-coverage-detail">
          <p className="text-white text-sm font-semibold mb-2">Avec Protect +, vous êtes couvert pour :</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {COVERED.map((c) => (
              <div key={c} className="flex items-center gap-2 text-slate-300 text-sm">
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />{c}
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-start gap-2 text-emerald-300 text-sm bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
            <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Économie immédiate de <strong>{savings}€</strong> sur votre caution. Le forfait s'arrête automatiquement à la fin de votre location.</span>
          </div>

          <label className="mt-4 flex items-start gap-2 cursor-pointer" data-testid="protect-accept-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => onAccept(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#DFFF00]"
              data-testid="protect-accept-checkbox"
            />
            <span className="text-slate-300 text-sm">
              J'ai lu et j'accepte les conditions de l'avenant{' '}
              <a href={PROTECT_PDF_URL} target="_blank" rel="noopener noreferrer" className="text-[#DFFF00] underline inline-flex items-center gap-1" data-testid="protect-pdf-link">
                Protect + <FileText className="w-3 h-3" />
              </a>.
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

export default ProtectPlusSelector;
